// Nexus Coins — bundle purchase shop (PayPal Smart Buttons + Google Pay)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Coins as CoinIcon, Sparkles, Plus, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import PayPalCheckout from "../components/PayPalCheckout";

export default function Coins() {
  const { user, refresh } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/coins/bundles").then(({ data }) => setBundles(data)); }, []);

  const mockBuy = async (b) => {
    if (!user) { toast.error("Sign in first"); return; }
    try {
      const { data } = await api.post("/coins/buy", { bundle_id: b.id });
      toast.success(`+${data.coins} NX added (demo)`); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purchase failed"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-12" data-testid="coins-page">
      <Toaster theme="dark" position="top-center" />
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <div className="label-eyebrow">// nexus coins</div>
        <h1 className="font-display font-black text-4xl lg:text-7xl tracking-tighter">NX <span className="text-nexus-cyan">coins</span>.</h1>
        <p className="text-white/50">Coins fuel everything — tips, paid stickers, sub gifts, BReels boosts. Real money via <strong className="text-nexus-cyan">PayPal · Google Pay · Card</strong> (Smart Buttons activate when owner adds keys; falls back to PayPal.Me).</p>
        {user && <div className="font-display tracking-wider text-sm">Current balance: <span className="text-nexus-cyan font-bold">{(user.wallet_balance || 0).toFixed(0)} NX</span></div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5">
        {bundles.map((b, i) => {
          const featured = i === 2;
          return (
            <div key={b.id} data-testid={`bundle-${b.id}`} className={`p-6 lg:p-8 ${featured ? "bg-nexus-panel border border-nexus-cyan" : "bg-nexus-surface"}`}>
              {featured && <div className="text-[9px] tracking-[0.3em] font-display uppercase text-nexus-cyan mb-3">★ Most Popular</div>}
              <div className="label-eyebrow">{b.label}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <CoinIcon className="w-5 h-5 text-nexus-amber" />
                <div className="font-display font-black text-4xl ml-1">{b.coins}</div>
                <div className="text-sm text-white/40">NX</div>
              </div>
              {b.bonus > 0 && <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-nexus-mint">+{b.bonus} bonus</div>}
              <div className="mt-6 font-display text-2xl">${b.price.toFixed(2)}</div>
              <div className="text-[10px] text-white/40 tracking-wider mt-1">${(b.price / (b.coins + (b.bonus || 0)) * 100).toFixed(2)} per 100 NX</div>
              <button onClick={() => user ? setActive(b) : toast.error("Sign in first")} className={featured ? "btn-nexus w-full justify-center mt-6" : "btn-ghost w-full justify-center mt-6"} data-testid={`buy-bundle-${b.id}`}>
                <Plus className="w-3.5 h-3.5" /> Buy
              </button>
            </div>
          );
        })}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setActive(null)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="coin-checkout-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl">Buy {active.coins + (active.bonus || 0)} NX</h3>
              <button onClick={() => setActive(null)}><X className="w-4 h-4 text-white/40" /></button>
            </div>
            <div className="text-[11px] text-white/50">{active.label} bundle · ${active.price}</div>
            <PayPalCheckout purpose="nx_bundle" amountUSD={active.price} refId={active.id}
              label={`Buy ${active.coins} NX`} onSuccess={() => { refresh(); setActive(null); toast.success("Coins on the way!"); }} />
            <div className="text-center text-[10px] text-white/30 tracking-wider">— or —</div>
            <button onClick={() => mockBuy(active)} className="btn-ghost w-full justify-center text-[10px]">Demo top-up (no payment)</button>
          </div>
        </div>
      )}

      <div className="glass p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-nexus-cyan" /><div className="label-eyebrow">// where coins go</div></div>
        <ul className="mt-3 text-sm text-white/70 space-y-2">
          <li>• Tip creators directly — 95% goes to them.</li>
          <li>• Send paid stickers — float on stream, hype the chat.</li>
          <li>• Become a supporter of a creator ($4.90, one-time, 30 days — separate from your Studio site sub).</li>
          <li>• Top up ad campaigns (sponsors).</li>
        </ul>
      </div>

      {/* ADVERTISER · IMPRESSION BUNDLES */}
      <ImpressionBundles />
    </div>
  );
}

function ImpressionBundles() {
  const PACKS = [
    { id: "imp_1k",   impressions: 1_000,   nx: 500,    aud: 6 },
    { id: "imp_5k",   impressions: 5_000,   nx: 2_400,  aud: 28, bonus: "−4%" },
    { id: "imp_10k",  impressions: 10_000,  nx: 4_500,  aud: 54, bonus: "−10%" },
    { id: "imp_50k",  impressions: 50_000,  nx: 21_000, aud: 252, bonus: "−16%" },
    { id: "imp_100k", impressions: 100_000, nx: 40_000, aud: 480, bonus: "−20%" },
  ];
  return (
    <div className="space-y-6" data-testid="impression-bundles">
      <div className="text-center space-y-2">
        <div className="label-eyebrow text-nexus-gold">// advertisers · impression bundles</div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">Buy <span className="text-nexus-gold">impression bundles</span> with NX.</h2>
        <p className="text-white/50 text-sm max-w-2xl mx-auto">Sponsors top up NX coins above then convert to impressions inside the Banner Editor. Discounts scale with volume.</p>
      </div>
      <div className="grid sm:grid-cols-5 gap-px bg-white/5">
        {PACKS.map((p, i) => (
          <div key={p.id} data-testid={`imp-${p.id}`} className={`p-5 ${i === 2 ? "bg-nexus-panel border border-nexus-gold" : "bg-nexus-surface"}`}>
            {i === 2 && <div className="text-[9px] tracking-[0.3em] uppercase text-nexus-gold font-display mb-2">★ Best Value</div>}
            <div className="label-eyebrow">{p.impressions.toLocaleString()} imp</div>
            <div className="font-display text-2xl mt-2 text-nexus-gold">{p.nx.toLocaleString()} NX</div>
            <div className="text-[10px] text-white/40 mt-1">≈ ${p.aud} AUD</div>
            {p.bonus && <div className="text-[10px] text-nexus-mint mt-1">{p.bonus}</div>}
          </div>
        ))}
      </div>
      <div className="text-center">
        <Link to="/sponsor" className="btn-nexus" data-testid="impression-go-sponsor"><Sparkles className="w-3.5 h-3.5" />Top up &amp; launch campaign</Link>
      </div>
    </div>
  );
}

